import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, Input, OnChanges, OnDestroy, OnInit } from '@angular/core';
import { NavigationStart, RouterModule } from '@angular/router';
import { IProductDataV2 } from '@df/catalog/product/product';
import { App } from '@df/core/app';
import { DfEventService } from '@df/core/service/df-event';
import { Status } from '@df/core/status';
import { Wishlist } from '@df/session/api/wishlist/wishlist';
import { OptionLabelPipe } from '@df/ui/common/option-label.pipe';
import { SizeClassDirective } from '@df/ui/common/size-class.directive';
import { TranslatePipe } from '@df/ui/translation/translate.pipe';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { filter, skip } from 'rxjs/operators';
import { ActionComponent } from '../../../../ui/api/action.component';
import { ProductDecorator } from '../../../model/product/product.decorator';
import {
    PRODUCT_CLOSE_PREVIEW_EVENT_NAME,
    PRODUCT_WISHLIST_ADD_EVENT_NAME,
    PRODUCT_WISHLIST_OPEN_PREVIEW_EVENT_NAME,
    PRODUCT_WISHLIST_PREPARE_PREVIEW_EVENT_NAME,
    ProductEventData
} from '../../product/product.abstract';
import { ProductService } from '../../product/product.service';
import { WishlistStatus } from '../wishlist.interface';

@Component({
    selector: 'wishlist-message',
    templateUrl: './wishlist-message.component.html',
    styleUrl: './wishlist-message.component.scss',
    standalone: true,
    imports: [ActionComponent, CommonModule, ICmsClientModule, OptionLabelPipe, RouterModule, SizeClassDirective, TranslatePipe],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class WishlistMessageComponent extends UiComponent implements OnInit, OnChanges, OnDestroy {
    @Input({ required: true })
    product!: ProductDecorator<IProductDataV2>;

    @Input()
    listPosition?: number;

    status = new Status();

    readonly wishlistStatus = WishlistStatus;
    currentStatus?: WishlistStatus;
    size?: number;
    protected closeTimeout?: number;

    protected app = inject(App);
    protected dfEventService = inject(DfEventService);
    protected productService = inject(ProductService);

    override ngOnInit(): void {
        super.ngOnInit();

        this.subscriptions.add(this.status.subscribe(() => this.detectChanges()));
        this.subscriptions.add(
            this.app.meta.routerEvents
                .pipe(
                    skip(1),
                    filter(e => e instanceof NavigationStart)
                )
                .subscribe(() => this.onCloseEvent())
        );

        this.subscriptions.add(
            this.dfEventService.success(event => {
                if (this.productService.canTriggerEvent(event, this.product, this.listPosition)) {

                    this.currentStatus = undefined;
                    this.detectChanges();

                    if (event.id === PRODUCT_WISHLIST_PREPARE_PREVIEW_EVENT_NAME) {
                        this.onPrepareEvent(event.data);
                    }

                    if (event.id === PRODUCT_WISHLIST_OPEN_PREVIEW_EVENT_NAME || event.id === PRODUCT_WISHLIST_ADD_EVENT_NAME) {
                        this.onOpenEvent(event.data);
                    }

                    if (event.id === PRODUCT_CLOSE_PREVIEW_EVENT_NAME) {
                        this.onCloseEvent(event.data);
                    }
                }
            })
        );
    }

    get wishlist(): Wishlist {
        return this.app.customer.wishlist;
    }

    get showLogin(): boolean {
        return this.currentStatus === WishlistStatus.AddSuccess && !this.app.customer.model.isLoggedIn;
    }

    get showBefore(): boolean {
        return this.currentStatus === WishlistStatus.BeforeAdd;
    }

    get showAlready(): boolean {
        return this.currentStatus === WishlistStatus.Already;
    }

    remove(id: number): void {
        const items = this.wishlist.getItemsByProduct(id)?.map(item => item.data.id) || [];

        this.wishlist.remove(this.status, items);
    }

    protected onPrepareEvent(data: ProductEventData): void {
        this.currentStatus = data?.status;

        this.detectChanges();
    }

    protected onOpenEvent(data: ProductEventData): void {
        this.currentStatus = data?.status;

        if (data.option || data.product.data?.size) {
            this.size = data.option?.value || data.product.data?.size;
        } else {
            this.size = undefined;
        }

        this.detectChanges();

        if (data.status === WishlistStatus.AddSuccess) {
            this.closeTimeout = this.timeoutService.setTimeout(() => {
                this.dfEventService.dispatch({
                    id: PRODUCT_CLOSE_PREVIEW_EVENT_NAME,
                    data: {
                        product: this.product,
                        index: this.listPosition
                    }
                });
            }, 5000);
        }
    }

    protected onCloseEvent(data?: ProductEventData): void {
        clearTimeout(this.closeTimeout);
        this.closeTimeout = undefined;

        this.currentStatus = undefined;

        this.detectChanges();
    }
}
